import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  browserStackDevices,
  kLanguages,
  kOrientations,
} from "../constants/appetize-constants";
import { AppetizeOSVersions } from "../utils/types";
import { AuthContext } from "../auth/AuthContext";
import { trackSentryException } from "../utils/helpers";
import { useSetAtom } from "jotai";
import { customAlertStateAtom } from "./custom-alert";
import { addDoc, collection } from "firebase/firestore";
import { customFirestore } from "../firebase";
import { Form, Formik } from "formik";
import { boolean, number, object, string, ValidationError } from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { ArrowBack, Check } from "@mui/icons-material";
import FormikTextInput from "../formikComponents/formik-text-input";
import FormikSelect from "../formikComponents/formik-select";
import { getCompanySettings } from "@/utils/firebase_operations";
import { useAtomValue } from "jotai";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import FormikCheckbox from "@/formikComponents/formik-checkbox";

interface DeviceConfigurationDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  appetizeOSVersions: AppetizeOSVersions;
  browserStackDevices: AppetizeOSVersions;
}

const TestDeviceConfigurationDialog = ({
  open,
  setOpen,
  appetizeOSVersions,
  browserStackDevices,
}: DeviceConfigurationDialogProps) => {
  const { user } = useContext<any>(AuthContext);
  const { data: companyData } = useAtomValue(companyDataAtom(user.companyId));

  const handleClose = useCallback(() => {
    setOpen((prev) => false);
  }, [setOpen]);

  const queryClient = useQueryClient();
  const setAlert = useSetAtom(customAlertStateAtom);

  const confirmHandler = useMutation({
    mutationFn: async (values: any) => {
      try {
        await addDoc(
          collection(customFirestore, "custom-test-device-configurations"),
          {
            ...values,
            organisationId: user.companyId,
          }
        );

        await queryClient.invalidateQueries({
          queryKey: ["getTestDeviceConfigurations"],
        });
        setAlert({ open: true, type: "success", title: "Configuration created successfully!" });
      } catch (e) {
        trackSentryException(e);
        setAlert({ open: true, type: "error", title: "An error occurred", maintainDisplayed: true });
      }
      handleClose();
    },
  });

  const initialValues = useMemo(() => {
    const platforms = Object.keys(appetizeOSVersions);
    const initialPlatform = platforms.at(0)!;
    const initialDevice = Object.keys(appetizeOSVersions[initialPlatform]).at(
      0
    )!;
    const initialOS = appetizeOSVersions[initialPlatform][initialDevice].at(-1);

    return {
      ...(companyData?.settings.displayBrowserStackIntegration && {
        isBrowserStackDevice: false,
      }),
      coordinates: { latitude: null, longitude: null },
      platform: initialPlatform,
      device: initialDevice,
      osVersion: initialOS,
      locale: "en_US",
      orientation: kOrientations.at(0),
    };
  }, [appetizeOSVersions, companyData]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={object()
          .shape({
            coordinates: object().shape({
              longitude: number()
                .nullable()
                .typeError("Longitude must be a number"),
              latitude: number()
                .nullable()
                .typeError("Latitude must be a number"),
            }),
            platform: string().nonNullable(),
            device: string().nonNullable(),
            osVersion: string().nonNullable(),
            locale: string().nonNullable(),
            orientation: string().nonNullable(),
            isBrowserStackDevice: boolean().nullable(),
          })
          .test(
            "longitude-and-latitude",
            "Longitude and Latitude should both be provided if one is given",
            function (obj) {
              const { longitude, latitude } = obj.coordinates;
              if ((longitude && !latitude) || (latitude && !longitude)) {
                return new ValidationError(
                  "Longitude and Latitude should both be provided if one is given",
                  null,
                  longitude != null
                    ? "coordinates.latitude"
                    : "coordinates.longitude"
                );
              }
              return true; // return true if validation passes
            }
          )}
        onSubmit={async (values, { setSubmitting }) => {
          const coordinates =
            values.coordinates?.longitude != null &&
            values.coordinates?.latitude != null
              ? {
                  latitude: parseFloat(values.coordinates.latitude),
                  longitude: parseFloat(values.coordinates.longitude),
                }
              : undefined;

          let valuesToUse: any = values;

          if (coordinates == null) {
            const { coordinates, ...valuesWithoutCoordinates } = values;
            valuesToUse = valuesWithoutCoordinates;
          }
          await confirmHandler.mutateAsync(valuesToUse);
          setSubmitting(false);
        }}
        validateOnBlur={false}
      >
        {({ isSubmitting, values, setValues }) => {
          const devices = values.isBrowserStackDevice
            ? browserStackDevices
            : appetizeOSVersions;
          return (
            <Form>
              <DialogTitle>Add Test Device Configuration</DialogTitle>
              <DialogContent dividers className={"flex items-start gap-4"}>
                <div className={"flex flex-1 flex-col gap-4"}>
                  {companyData?.settings.displayBrowserStackIntegration && (
                    <FormikCheckbox
                      name={`isBrowserStackDevice`}
                      label={"BrowserStack Device"}
                      type={"checkbox"}
                      onChange={(value: boolean) => {
                        const devices = value
                          ? browserStackDevices
                          : appetizeOSVersions;

                        const newDevice = Object.keys(
                          devices[values.platform]
                        ).at(0)!;
                        const newOS = devices[values.platform][newDevice].at(0);

                        setValues((prev) => ({
                          ...prev,
                          device: newDevice,
                          osVersion: newOS,
                        }));
                      }}
                    />
                  )}
                  <FormikSelect
                    name={"platform"}
                    key={"platform"}
                    label={"Platform"}
                    enableCapitalize={false}
                    options={Object.keys(devices)}
                    onChange={(value: string) => {
                      setValues((prev) => {
                        const newDevice = Object.keys(devices[value]).at(0)!;
                        const newOS = devices[value][newDevice].at(0);
                        return {
                          ...prev,
                          platform: value,
                          device: newDevice,
                          osVersion: newOS,
                        };
                      });
                    }}
                  />
                  <FormikSelect
                    name={"device"}
                    key={`${values.platform}-device`}
                    enableCapitalize={false}
                    label={"Device"}
                    options={Object.keys(devices[values.platform])}
                    onChange={(value: string) => {
                      setValues((prev) => {
                        const newOS = devices[prev.platform][value].at(-1);
                        return {
                          ...prev,
                          device: value,
                          osVersion: newOS,
                        };
                      });
                    }}
                  />
                  <FormikSelect
                    name={"osVersion"}
                    key={`${values.platform}-${values.device}-os`}
                    label={"OS Version"}
                    options={
                      devices[values.platform][values.device] ??
                      devices[values.platform][
                        Object.keys(devices[values.platform]).at(0)!
                      ]
                    }
                  />
                  <FormikSelect
                    name={"locale"}
                    key={"locale"}
                    label={"Language"}
                    options={kLanguages}
                    enableCapitalize={false}
                  />
                </div>
                <div className={"flex flex-1 flex-col gap-[19px]"}>
                  <FormikTextInput
                    label="Latitude"
                    name="coordinates.latitude"
                    type="text"
                    size={"small"}
                  />
                  <FormikTextInput
                    label="Longitude"
                    name="coordinates.longitude"
                    type="text"
                    size={"small"}
                  />
                  <FormikSelect
                    name={"orientation"}
                    key={"orientation"}
                    label={"Orientation"}
                    options={kOrientations}
                    enableCapitalize={false}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
                <Button
                  type={"submit"}
                  disabled={isSubmitting}
                  variant={"contained"}
                  startIcon={<Check />}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default TestDeviceConfigurationDialog;
